import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon, faBars, faTimes, faUser, faSignOutAlt, faCaretDown, faGlobe } from '@fortawesome/free-solid-svg-icons';
import styles from './Navbar.module.css';

const translations = {
    en: {
        dashboard: 'Dashboard',
        process: 'Process',
        features: 'Features',
        pricing: 'Pricing',
        faqs: 'FAQs',
        welcome: '',
        logout: 'SignOut',
        loginRegister: 'SignOn',
        settings: 'Settings',
        help: 'Help',
        about: 'About',
        traderAgent: 'ONSERV Trader 2026',
    },
    fr: {
        dashboard: 'Tableau de bord',
        process: 'Processus',
        features: 'Fonctionnalités',
        pricing: 'Prix',
        faqs: 'FAQs',
        welcome: '',
        logout: 'Déconnexion',
        loginRegister: 'Connexion / Inscription',
        settings: 'Paramètres',
        help: 'Aide',
        about: 'À propos',
        traderAgent: 'ONSERV Trader 2026',
    },
};

const Navbar = ({ onLoginClick, isAuth, pathname, onOpenSettings, onOpenHelp, onOpenAbout }) => {
    // Hooks MUST be inside the component
    const { theme, setTheme } = useTheme();
    const { user, logout } = useAuth();
    const { language, setLanguage } = useLanguage();
    
    const t = translations[language];
    const [isOpen, setIsOpen] = useState(false);
    const [isDashboardDropdownOpen, setIsDashboardDropdownOpen] = useState(false);
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);

    const toggleMenu = () => setIsOpen(!isOpen);

    const handleLogout = () => {
        logout();
        setIsOpen(false);
    };

    const handleDashboardClick = (e) => {
        e.preventDefault();
        setIsDashboardDropdownOpen(!isDashboardDropdownOpen);
    };

    const handleLanguageChange = (lang) => {
        setLanguage(lang);
        setIsLanguageDropdownOpen(false);
    };
    
    const handleDashboardLinkClick = (action) => {
        setIsDashboardDropdownOpen(false);
        action();
    };

    const handleThemeChange = (selectedTheme) => {
        setTheme(selectedTheme);
        setIsThemeDropdownOpen(false);
    };

    const isDashboardPage = pathname === '/dashboard' || pathname === '/';

    return (
        <nav className={styles.navbar}>
            <div className={styles.logo}>
                <a href="/"><span className="text-gradient">{t.traderAgent}</span></a>
            </div>
            
            <div className={`${styles.navLinks} ${isOpen ? styles.active : ''}`}>
                {isAuth ? (
                    isDashboardPage ? (
                        <div className={styles.dropdown}>
                            <a href="#" className={styles.navbarLink} onClick={handleDashboardClick}>
                                {t.dashboard} <FontAwesomeIcon icon={faCaretDown} />
                            </a>
                            {isDashboardDropdownOpen && (
                                <div className={styles.dropdownContent}>
                                    <a onClick={() => handleDashboardLinkClick(onOpenSettings)} className={styles.dropdownLink}>{t.settings}</a>
                                    <a onClick={() => handleDashboardLinkClick(onOpenHelp)} className={styles.dropdownLink}>{t.help}</a>
                                    <a onClick={() => handleDashboardLinkClick(onOpenAbout)} className={styles.dropdownLink}>{t.about}</a>
                                </div>
                            )}
                        </div>
                    ) : (
                        <a href="/" className={styles.navbarLink} onClick={() => setIsOpen(false)}>{t.dashboard}</a>
                    )
                ) : null}
                
                {/* Language Selector */}
                <div className={styles.dropdown}>
                    <a className={styles.dropdownLink} onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}>
                        <FontAwesomeIcon icon={faGlobe} /> {language.toUpperCase()}
                    </a>
                    {isLanguageDropdownOpen && (
                        <div className={styles.dropdownContent}>
                            <a onClick={() => handleLanguageChange('en')} className={styles.dropdownLink}>EN</a>
                            <a onClick={() => handleLanguageChange('fr')} className={styles.dropdownLink}>FR</a>
                        </div>
                    )}
                </div>

                {/* Theme Selector */}
                <div className={styles.dropdown}>
                    <a className={styles.dropdownLink} onClick={() => setIsThemeDropdownOpen(!isThemeDropdownOpen)}>
                        <FontAwesomeIcon icon={theme === 'day' ? faSun : faMoon} />
                    </a>
                    {isThemeDropdownOpen && (
                        <div className={styles.dropdownContent}>
                            <a onClick={() => handleThemeChange('day')} className={styles.dropdownLink}>
                                <FontAwesomeIcon icon={faSun} /> Day
                            </a>
                            <a onClick={() => handleThemeChange('night')} className={styles.dropdownLink}>
                                <FontAwesomeIcon icon={faMoon} /> Night
                            </a>
                        </div>
                    )}
                </div>

                {/* Auth Section */}
                {user ? (
                    <div className={styles.userSection}>
                        <span className={styles.welcomeText}>
                            <FontAwesomeIcon icon={faUser} />
                            {user.displayName || user.email.split('@')[0]}
                        </span>
                        <a onClick={handleLogout} className={styles.logoutButton}>
                            <FontAwesomeIcon icon={faSignOutAlt} />
                            {t.logout}
                        </a>
                    </div>
                ) : (
                   <a 
      onClick={() => {
        onLoginClick();
      }} 
      className={styles.loginButton}
    >
      {t.loginRegister}
    </a>
                )}
            </div>
            
            <div className={styles.menuToggle} onClick={toggleMenu}>
                <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
            </div>
        </nav>
    );
};

Navbar.propTypes = {
    onLoginClick: PropTypes.func.isRequired,
    isAuth: PropTypes.bool.isRequired,
    pathname: PropTypes.string.isRequired,
    onOpenSettings: PropTypes.func.isRequired,
    onOpenAbout: PropTypes.func.isRequired,
};

export default Navbar;